import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  Snackbar,
  CardHeader,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instance, InstanceConfiguration, OrderlySystem, client } from '../../app/services/api/orderManagementClient';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import useLogError from '../../hooks/useLogError';

const InstanceFormPage: FC = () => {
  const { t } = useTranslation('instances');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { logError } = useLogError();

  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<Instance>({
    id: 0,
    name: '',
    userName: '',
    domainName: '',
    storeSslRoot: '',
    apiKey: '',
    configuration: {
      isOrderlySaaS: false,
      uomGroup: '',
      brand: '',
      production: false,
      sendGridEmailTemplate: '',
      system: OrderlySystem.OFS,
    },
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  useEffect(() => {
    if (isEditMode && id) {
      loadInstance(id);
    }
  }, [isEditMode, id]);

  const loadInstance = async (instanceId: string) => {
    setIsLoading(true);
    try {
      const result = await client.getInstance(instanceId);
      if (result?.data) {
        setFormData(result.data);
      }
    } catch (error) {
      logError(error);
      setMessage(t('loadErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof Instance) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleConfigurationChange = (field: keyof InstanceConfiguration) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
    }));
  };

  const handleSelectChange = (field: keyof InstanceConfiguration) => (event: { target: { value: unknown } }) => {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        [field]: event.target.value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      if (isEditMode && id) {
        await client.updateInstance(id, formData);
        setMessage(t('updateSuccessMessage'));
      } else {
        await client.createInstance(formData);
        setMessage(t('createSuccessMessage'));
      }
      setIsSuccess(true);
      setShowMessage(true);
      setTimeout(() => {
        navigate('/instances');
      }, 500);
    } catch (error) {
      logError(error);
      setMessage(isEditMode ? t('updateErrorMessage') : t('createErrorMessage'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/instances');
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEditMode ? 'editInstance' : 'createInstance')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        {/* Basic Instance Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('basicInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid
              container
              spacing={8}>
              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('name')}
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('userName')}
                  value={formData.userName}
                  onChange={handleInputChange('userName')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}>
                <TextField
                  fullWidth
                  label={t('domainName')}
                  value={formData.domainName}
                  onChange={handleInputChange('domainName')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}>
                <TextField
                  fullWidth
                  label={t('storeSslRoot')}
                  value={formData.storeSslRoot}
                  onChange={handleInputChange('storeSslRoot')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}>
                <TextField
                  fullWidth
                  label={t('apiKey')}
                  value={formData.apiKey}
                  onChange={handleInputChange('apiKey')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Configuration Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('configuration')}
            sx={{ mb: 3, color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>

            <Grid
              container
              spacing={8}>
              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('uomGroup')}
                  value={formData.configuration?.uomGroup || ''}
                  onChange={handleConfigurationChange('uomGroup')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('brand')}
                  value={formData.configuration?.brand || ''}
                  onChange={handleConfigurationChange('brand')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <FormControl
                  fullWidth>
                  <InputLabel>{t('system')}</InputLabel>
                  <Select
                    value={formData.configuration?.system || OrderlySystem.OFS}
                    label={t('system')}
                    onChange={handleSelectChange('system')}
                    sx={{ 
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value={OrderlySystem.OFS}>OFS</MenuItem>
                    <MenuItem value={OrderlySystem.OFD}>OFD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <TextField
                  fullWidth
                  label={t('sendGridEmailTemplate')}
                  value={formData.configuration?.sendGridEmailTemplate || ''}
                  onChange={handleConfigurationChange('sendGridEmailTemplate')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.configuration?.isOrderlySaaS || false}
                      onChange={handleConfigurationChange('isOrderlySaaS')}
                    />
                  }
                  label={
                    <Typography
                      variant="textMD"
                      sx={{ ml: 1 }}>
                      {t('isOrderlySaaS')}
                    </Typography>
                  }
                  sx={{ 
                    m: 0,
                    p: 2,
                    backgroundColor: 'background.paper',
                    alignItems: 'center',
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.configuration?.production || false}
                      onChange={handleConfigurationChange('production')}
                    />
                  }
                  label={
                    <Typography
                      variant="textMD"
                      sx={{ ml: 1 }}>
                      {t('production')}
                    </Typography>
                  }
                  sx={{ 
                    m: 0,
                    p: 2,
                    backgroundColor: 'background.paper',
                    alignItems: 'center',
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSaving}
                sx={{ minWidth: 120, borderRadius: 2 }}
              >
                {isSaving ? t('saving') : t(isEditMode ? 'update' : 'create')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default InstanceFormPage;
